import config from "./config";

const BASE_URL = config.API_BASE_URL;

function resolveUrl(path) {
  if (!path) {
    return '';
  }

  const isAbsolute = /^https?:\/\//i.test(path);
  if (isAbsolute) {
    return path;
  }

  if (!BASE_URL) {
    return path.startsWith('/') ? path : `/${path}`;
  }

  if (path.startsWith('/')) {
    return `${BASE_URL}${path}`;
  }

  return `${BASE_URL}/${path}`;
}

function parseEventData(raw) {
  if (raw == null || raw === '') {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (err) {
    return raw;
  }
}

export function subscribeToFileProcessing(fileId, {
  onProgress,
  onRequirements,
  onComplete,
  onError,
} = {}) {
  if (!fileId) {
    console.warn('subscribeToFileProcessing: fileId is required');
    return () => {};
  }

  const eventSource = new EventSource(resolveUrl(`/sse/progress/${fileId}`), { withCredentials: true });

  const stop = () => {
    eventSource.close();
  };

  if (typeof onProgress === 'function') {
    eventSource.addEventListener('progress', (event) => {
      const payload = parseEventData(event.data);
      const percent = typeof payload === 'number' ? payload : Number(payload);
      onProgress(Number.isFinite(percent) ? percent : null);
    });
  }

  if (typeof onRequirements === 'function') {
    eventSource.addEventListener('requirements', (event) => {
      const payload = parseEventData(event.data);
      const items = Array.isArray(payload) ? payload : (payload != null ? [payload] : []);
      if (items.length > 0) {
        onRequirements(items);
      }
    });
  }

  eventSource.addEventListener('complete', (event) => {
    const payload = parseEventData(event.data);
    if (typeof onComplete === 'function') {
      onComplete(payload);
    }
    stop();
  });

  eventSource.onerror = (error) => {
    if (typeof onError === 'function') {
      onError(error);
    }
    stop();
  };

  return stop;
}

export function subscribeToConflicts(fileId, {
  onConflict,
  onConflictPage,
  onValidation,
  onComplete,
  onError,
} = {}) {
  if (!fileId) {
    console.warn('subscribeToConflicts: fileId is required');
    return () => {};
  }

  if (typeof window !== 'undefined' && typeof window.EventSource === 'undefined') {
    console.warn('EventSource is not supported in this environment. Falling back.');
    if (typeof onError === 'function') {
      setTimeout(() => onError(new Error('EventSource is not supported')), 0);
    }
    return () => {};
  }

  const eventSource = new EventSource(resolveUrl(`/sse/conflicts/${fileId}`), { withCredentials: true });

  const stop = () => {
    eventSource.close();
  };

  if (typeof onConflict === 'function') {
    eventSource.addEventListener('conflict', (event) => {
      const payload = parseEventData(event.data);
      if (payload != null) {
        onConflict(payload);
      }
    });
  }

  if (typeof onConflictPage === 'function') {
    eventSource.addEventListener('conflict-page', (event) => {
      const payload = parseEventData(event.data);
      const items = Array.isArray(payload)
        ? payload
        : (payload != null ? [payload] : []);
      if (items.length > 0) {
        onConflictPage(items);
      }
    });
  }

  if (typeof onValidation === 'function') {
    eventSource.addEventListener('validation', (event) => {
      const payload = parseEventData(event.data);
      const items = Array.isArray(payload)
        ? payload
        : (payload != null ? [payload] : []);
      if (items.length > 0) {
        onValidation(items);
      }
    });
  }

  eventSource.addEventListener('complete', (event) => {
    const payload = parseEventData(event.data);
    if (typeof onComplete === 'function') {
      onComplete(payload);
    }
    stop();
  });

  eventSource.onerror = (error) => {
    if (typeof onError === 'function') {
      onError(error);
    }
    stop();
  };

  return stop;
}

export function subscribeToPromptGeneration(projectId, jobId, {
  onProgress,
  onReset,
  onRequirementsChunk,
  onConflictsChunk,
  onMessage,
  onComplete,
  onError,
} = {}) {
  if (!projectId || !jobId) {
    console.warn('subscribeToPromptGeneration: projectId and jobId are required');
    return () => {};
  }

  const eventSource = new EventSource(resolveUrl(`/sse/projects/${projectId}/prompts/${jobId}`), { withCredentials: true });

  const stop = () => {
    eventSource.close();
  };

  if (typeof onProgress === 'function') {
    eventSource.addEventListener('progress', (event) => {
      onProgress(parseEventData(event.data));
    });
  }

  if (typeof onReset === 'function') {
    eventSource.addEventListener('reset', (event) => {
      onReset(parseEventData(event.data));
    });
  }

  if (typeof onRequirementsChunk === 'function') {
    eventSource.addEventListener('requirements-chunk', (event) => {
      onRequirementsChunk(parseEventData(event.data));
    });
  }

  if (typeof onConflictsChunk === 'function') {
    eventSource.addEventListener('conflicts-chunk', (event) => {
      onConflictsChunk(parseEventData(event.data));
    });
  }

  if (typeof onMessage === 'function') {
    eventSource.addEventListener('message', (event) => {
      onMessage(parseEventData(event.data));
    });
  }

  eventSource.addEventListener('complete', (event) => {
    const payload = parseEventData(event.data);
    if (typeof onComplete === 'function') {
      onComplete(payload);
    }
    stop();
  });

  const handleError = (event) => {
    const payload = event && 'data' in event ? parseEventData(event.data) : null;
    if (typeof onError === 'function') {
      if (payload != null) {
        onError(payload);
      } else {
        onError(event);
      }
    }
    stop();
  };

  eventSource.addEventListener('error', handleError);
  eventSource.onerror = handleError;

  return stop;
}

export function setupSSE(source, onMessage, onError) {
  if (!source) {
    console.warn('setupSSE: source is required');
    return () => {};
  }

  const handleError = typeof onError === 'function' ? onError : () => {};

  const looksLikeUrl = typeof source === 'string' && (/^https?:\/\//i.test(source) || source.startsWith('/'));

  if (!looksLikeUrl) {
    const eventSource = new EventSource(resolveUrl(`/sse/progress/${encodeURIComponent(source)}`), { withCredentials: true });

    const stop = () => {
      eventSource.close();
    };

    eventSource.addEventListener('progress', (event) => {
      if (typeof onMessage === 'function') {
        const payload = parseEventData(event.data);
        const percent = typeof payload === 'number' ? payload : Number(payload);
        onMessage(Number.isFinite(percent) ? percent : null, event);
      }
    });

    eventSource.addEventListener('complete', () => {
      stop();
    });

    eventSource.onerror = (error) => {
      handleError(error);
      stop();
    };

    return stop;
  }

  const eventSource = new EventSource(resolveUrl(source), { withCredentials: true });

  const stop = () => {
    eventSource.close();
  };

  if (typeof onMessage === 'function') {
    eventSource.onmessage = onMessage;
  }

  eventSource.onerror = (error) => {
    handleError(error);
  };

  return stop;
}
